// src/generators/dzial9_prawdopodobienstwo/temat2_rachunek/podtemat2_zastosowaniaKombinatoryki_types/UrnTwoDrawsTask.js
import React, { useContext } from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { ThemeContext } from "../../../../context/ThemeContext";

// --- utils ---
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }

// akceptuje: całkowite, a/b, dziesiętne (z kropką lub przecinkiem)
function parseFraction(raw) {
  const s0 = String(raw).trim();
  const s = s0.replace(",", "."); // pozwól na przecinek

  // liczba całkowita
  if (/^[+-]?\d+$/.test(s)) return [parseInt(s, 10), 1];

  // ułamek a/b
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) {
    const den = parseInt(m[2], 10);
    if (den === 0) return null;
    return [parseInt(m[1], 10), den];
  }

  // liczba dziesiętna (np. .6, 0.6, 1.25)
  const d = s.match(/^[+-]?(?:\d+|\d*\.\d+)$/);
  if (d) {
    const sign = s.startsWith("-") ? -1 : 1;
    const body = s.replace(/^[+-]/, "");
    const [intPart, fracPart = ""] = body.split(".");
    const k = (fracPart || "").length;
    const p = sign * (parseInt(intPart || "0", 10) * (10 ** k) + parseInt(fracPart || "0", 10));
    const q = 10 ** k;
    return [p, q];
  }

  return null;
}

// kombinacje
function nCr(n, r) {
  if (r < 0 || r > n) return 0;
  r = Math.min(r, n - r);
  let num = 1, den = 1;
  for (let i = 1; i <= r; i++) { num *= (n - r + i); den *= i; }
  return Math.round(num / den);
}

// --- warianty zdarzeń ---
const MODES = [
  { key: "WW",   title: "obie białe",               latex: "2\\,\\text{białe}",         fav: ({W})   => nCr(W,2) },
  { key: "BB",   title: "obie czarne",              latex: "2\\,\\text{czarne}",        fav: ({B})   => nCr(B,2) },
  { key: "WB",   title: "różnokolorowe",            latex: "\\text{różnokolorowe}",     fav: ({W,B}) => W*B },
  // \ge w trybie matematycznym + słowo w \text{...}
  { key: "GE1W", title: "co najmniej jedna biała",  latex: "\\ge 1\\,\\text{ biała}",   fav: ({W,B}) => nCr(W+B,2) - nCr(B,2) },
  { key: "GE1B", title: "co najmniej jedna czarna", latex: "\\ge 1\\,\\text{ czarna}",  fav: ({W,B}) => nCr(W+B,2) - nCr(W,2) },
];

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// prosta „wizualizacja” paska kul (dla klimatu i motywu)
function BallsBar({ W, B }) {
  const { theme } = useContext(ThemeContext);
  const border = theme === "light" ? "#000" : "#fff";
  const whiteFill = theme === "light" ? "#fff" : "#ddd";
  const blackFill = theme === "light" ? "#111" : "#000";
  const cell = { width: 18, height: 18, borderRadius: "50%", margin: "2px", border: `1px solid ${border}` };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "8px 0" }}>
      {Array.from({length: W}).map((_,i) => <div key={`w${i}`} style={{...cell, background: whiteFill}} />)}
      {Array.from({length: B}).map((_,i) => <div key={`b${i}`} style={{...cell, background: blackFill}} />)}
    </div>
  );
}

// ===== core =====
function generateOne() {
  // losujemy parametry z sensownymi ograniczeniami
  let W = 3 + Math.floor(Math.random()*4); // 3..6
  let B = 2 + Math.floor(Math.random()*5); // 2..6
  if (W < 2) W = 2;
  if (B < 2) B = 2;
  const N = W + B;
  const mode = pick(MODES);

  const omega = nCr(N, 2);
  const fav = mode.fav({ W, B });
  const [p, q] = simplify(fav, omega);

  return { W, B, N, omega, fav, p, q, modeKey: mode.key, modeTitle: mode.title, modeLatex: mode.latex };
}

export function generateTask() {
  const d = generateOne();
  return {
    type: "UrnTwoDrawsTask",
    data: d,
    uid: `urn2_${d.modeKey}_${d.W}_${d.B}_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", margin: "12px 0" }}>
      <div>
        <span>W urnie znajduje się </span>
        <InlineMath math={`${data.W}`} />
        <span> kul białych i </span>
        <InlineMath math={`${data.B}`} />
        <span> kul czarnych. Losujemy </span>
        <InlineMath math={"2"} />
        <span> kule </span>
        <strong>bez zwracania</strong>
        <span>. Oblicz prawdopodobieństwo, że wylosowane kule będą: </span>
        <strong>{data.modeTitle}</strong>
        <span>.</span>
      </div>
      <BallsBar W={data.W} B={data.B} />
    </div>
  );
}

// podgląd przy inpucie: P(A) = …
export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3">
      <span><InlineMath math={'P(A) = '} /></span>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="P(A)"
        placeholder="np. 3/5 lub 0.6"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return { correct: false, message: "Podaj ułamek (np. 3/10) lub liczbę (np. 0.3)." };
  }
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct: false, message: "Nieprawidłowy ułamek/liczba." };

  const [gp, gq] = parsed;
  const [cp, cq] = simplify(data.p, data.q);
  const [gpp, gqq] = simplify(gp, gq);

  // dokładny ułamek
  let correct = (gpp === cp && gqq === cq);

  // tolerancja na wartość dziesiętną ±0.01
  if (!correct) {
    const userVal = gp / gq;
    const correctVal = cp / cq;
    if (Math.abs(userVal - correctVal) <= 0.01 + 1e-12) correct = true;
  }

  return { correct, expected: `\\tfrac{${cp}}{${cq}}` };
}

// poprawna odp: duży ułamek
export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={`P(A) = ${dfrac(data.p, data.q)}`} />
    </div>
  );
}

// user answer z podpisem P(A)
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={formatLatexPreview(userAnswer.raw, 'P(A)')} />
    </div>
  );
}

// ——— KROTKIE OPISY DOPASOWANE DO WARIANTU ———
function ModeExplanation({ data }) {
  const { modeKey, W, B, N } = data;

  if (modeKey === "WW") {
    return (
      <div>
        Wybieramy <b>2 białe</b> spośród <InlineMath math={`${W}`} /> białych,
        czyli liczba par białych to <InlineMath math={`\\binom{${W}}{2}`} />.
      </div>
    );
  }
  if (modeKey === "BB") {
    return (
      <div>
        Wybieramy <b>2 czarne</b> spośród <InlineMath math={`${B}`} /> czarnych,
        więc <InlineMath math={`\\binom{${B}}{2}`} />.
      </div>
    );
  }
  if (modeKey === "WB") {
    return (
      <div>
        Para <b>różnokolorowa</b>: wybieramy jedną białą i jedną czarną,
        zatem <InlineMath math={`${W}\\cdot${B}`} /> możliwości.
      </div>
    );
  }
  if (modeKey === "GE1W") {
    return (
      <div>
        <b>Co najmniej jedna biała</b>: bierzemy wszystkie pary{" "}
        <InlineMath math={`\\binom{${N}}{2}`} /> i odejmujemy pary bez białych
        (same czarne) <InlineMath math={`\\binom{${B}}{2}`} />.
      </div>
    );
  }
  // GE1B
  return (
    <div>
      <b>Co najmniej jedna czarna</b>: bierzemy wszystkie pary{" "}
      <InlineMath math={`\\binom{${N}}{2}`} /> i odejmujemy pary bez czarnych
      (same białe) <InlineMath math={`\\binom{${W}}{2}`} />.
    </div>
  );
}

// Wyjaśnienie: Ω → |A| → P(A)
export function renderExplanation({ data }) {
  const { W, B, N, omega, fav } = data;
  const [simpNum, simpDen] = simplify(fav, omega);
  const tail = (simpNum === fav && simpDen === omega) ? "" : `= \\dfrac{${simpNum}}{${simpDen}}`;

  // krok 1 (Ω)
  const omegaLine = `\\Omega = \\{\\text{wszystkie 2-elementowe wybory z } ${N} \\text{ kul bez zwracania}\\},\\; |\\Omega| = \\binom{${N}}{2} = ${omega}`;

  // krok 2 (|A|) — z dodatkowym etapem dla odejmowania
  const cN2 = nCr(N, 2);
  const cW2 = nCr(W, 2);
  const cB2 = nCr(B, 2);

  let aCountLine = "";
  if (data.modeKey === "WW") {
    aCountLine = `|\\mathrm{A}| = \\binom{${W}}{2} = ${cW2}`;
  } else if (data.modeKey === "BB") {
    aCountLine = `|\\mathrm{A}| = \\binom{${B}}{2} = ${cB2}`;
  } else if (data.modeKey === "WB") {
    aCountLine = `|\\mathrm{A}| = ${W}\\cdot${B} = ${fav}`;
  } else if (data.modeKey === "GE1W") {
    aCountLine = `|\\mathrm{A}| = \\binom{${N}}{2} - \\binom{${B}}{2} = ${cN2} - ${cB2} = ${fav}`;
  } else if (data.modeKey === "GE1B") {
    aCountLine = `|\\mathrm{A}| = \\binom{${N}}{2} - \\binom{${W}}{2} = ${cN2} - ${cW2} = ${fav}`;
  }

  // krok 3 (P)
  const probLine  = `P(\\mathrm{A}) = \\dfrac{|\\mathrm{A}|}{|\\Omega|} = \\dfrac{${fav}}{${omega}} ${tail}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1 — przestrzeń zdarzeń</strong>
        <BlockMath math={omegaLine} />
      </div>

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2 — liczymy liczność zdarzenia A</strong>
        <div><em>Zdarzenie:</em> <InlineMath math={data.modeLatex} /></div>
        <ModeExplanation data={data} />
        <BlockMath math={aCountLine} />
      </div>

      <div>
        <strong>Krok 3 — prawdopodobieństwo</strong>
        <BlockMath math={probLine} />
      </div>
    </div>
  );
}
